import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Header from "../Header";
import Field from "../../../../../Settings/Content/Components/Field";
import TrashBucketIcon from "../../../../../../Components/Icon-SVG/TrashBucketIcon";
import Tooltip from "../../../../../../Components/common/Tooltip/Tooltip";
import Backdrop from "../../../../../../Components/UI/Backdrop/Backdrop";

import { RequestUpdateAgentPolicy } from "../../../../../../store/actions/integrationsActions";
import { addNotification } from "../../../../../../store/actions/notifierActions";

const MIN_MAX_VALUES = {
  minFileSize: [1500, 4500],
  maxFileSize: [20800000, 62400000],
  cfboxScanThreshold: [200, 600],
};

const SCALAR_FIELDS = ["fallbackUrl", "minFileSize", "maxFileSize", "cfboxScanThreshold"];
const CSV_FIELDS = ["fileExtensions"];

const toBool = (v) => v === true || v === "true" || v === "True";
const toArray = (v) =>
  typeof v === "string"
    ? v
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : Array.isArray(v)
      ? v
      : [];

const IOCForm = ({ selectedHost }) => {
  const dispatch = useDispatch();

  const policy = useSelector((state) => state.integrations.agentPolicy);
  const updatePolicyLoading = useSelector((state) => state.integrations.updatePolicyLoading);

  const backend = policy?.settings?.ioc;

  const [iocUI, setIocUI] = useState({
    ...backend,
    fileExtensions: toArray(backend?.fileExtensions),
  });
  const [buffers, setBuffers] = useState({ fileExtensions: "" });

  const numberChangeHandler = (e) => {
    const { name, value } = e.target;
    setIocUI((prev) => ({ ...prev, [name]: value === "" ? "" : Number(value) }));
  };

  const textChangeHandler = (e) => {
    const { name, value } = e.target;
    setIocUI((prev) => ({ ...prev, [name]: value }));
  };

  const boolChangeHandler = (e) => {
    const { name, value } = e.target;
    setIocUI((prev) => ({ ...prev, [name]: value === "true" }));
  };

  const blurHandler = (e) => {
    const { name, value } = e.target;
    const limits = MIN_MAX_VALUES[name];
    if (!limits) return;

    const [min, max] = limits;
    let newValue = Number(value);

    if (newValue < min) newValue = min;
    if (newValue > max) newValue = max;

    setIocUI((prev) => ({ ...prev, [name]: newValue }));
  };

  const bufferChangeHandler = (name, value) => {
    setBuffers((prev) => ({ ...prev, [name]: value }));
  };

  const addCsvItem = (name) => {
    const item = (buffers[name] || "").trim();

    if (item === "") {
      dispatch(
        addNotification({
          message: "Enter a valid value",
          options: { variant: "error" },
          time: 3000,
        }),
      );
      return;
    }
    if ((iocUI[name] || []).some((existing) => existing === item)) {
      dispatch(
        addNotification({
          message: "Value already exists",
          options: { variant: "error" },
          time: 3000,
        }),
      );
      return;
    }

    setIocUI((prev) => ({ ...prev, [name]: [...(prev[name] || []), item] }));
    setBuffers((prev) => ({ ...prev, [name]: "" }));
  };

  const removeCsvItem = (name, index) => {
    setIocUI((prev) => ({ ...prev, [name]: prev[name].filter((_, i) => i !== index) }));
  };

  const checkSaveButtonDisabled = () => {
    if (!backend || !iocUI) return true;

    for (const f of SCALAR_FIELDS) {
      if (backend[f] !== iocUI[f]) {
        return false;
      }
    }

    for (const f of CSV_FIELDS) {
      const backendArr = toArray(backend[f]).slice().sort();
      const uiArr = [...(iocUI[f] || [])].sort();
      if (backendArr.length !== uiArr.length) return false;
      if (!backendArr.every((v, i) => v === uiArr[i])) return false;
    }

    return true;
  };

  const updateAgentSettingsHandler = (e) => {
    e.preventDefault();
    if (updatePolicyLoading) return;

    dispatch(
      RequestUpdateAgentPolicy({
        id: policy.id,
        settings: {
          ...policy.settings,
          ioc: {
            ...iocUI,
            fileExtensions: iocUI.fileExtensions.join(","),
          },
        },
        host: selectedHost,
      }),
    );
  };

  const toggleStatusHandler = () => {
    dispatch(
      RequestUpdateAgentPolicy({
        id: policy.id,
        settings: {
          ...policy.settings,
          ioc: {
            ...policy.settings.ioc,
            enabled: !policy?.settings?.ioc?.enabled,
          },
        },
        host: selectedHost,
      }),
    );
  };

  const renderCsvList = (name, label, tooltip, placeholder) => (
    <>
      <div className="agent-policy-form__group-line">
        <Field
          label={label}
          tooltipText={tooltip}
          width="300px"
          value={buffers[name]}
          onChange={(e) => bufferChangeHandler(name, e.target.value)}
          parentClassName="p-r-20"
          placeholder={placeholder}
        />
        <button
          type="button"
          className="products-body-form__button"
          onClick={() => addCsvItem(name)}>
          +
        </button>
      </div>

      <ul className="agent-policy-form__list" style={{ gridColumn: "span 2" }}>
        {iocUI?.[name]?.length > 0 ? (
          iocUI[name].map((item, index) => (
            <li key={index} className="agent-policy-form__list-item">
              {item}
              <button
                type="button"
                style={{ display: "flex" }}
                onClick={() => removeCsvItem(name, index)}>
                <TrashBucketIcon width="16" height="16" />
                <p style={{ marginLeft: "5px" }}>Delete</p>
              </button>
            </li>
          ))
        ) : (
          <li className="no-data">&nbsp;No Data</li>
        )}
      </ul>
    </>
  );

  return (
    <>
      <Header
        policy={policy}
        selectedHost={selectedHost}
        toggleProps={{
          show: true,
          text: "IOC Status",
          active: policy?.settings?.ioc?.enabled,
          onClick: toggleStatusHandler,
        }}
      />

      <form className="agent-policy-form">
        <div className="agent-policy-form__line">
          <Field
            label="Min File Size in bytes"
            tooltipText="Min: 1500, Max: 4500"
            width="220px"
            name="minFileSize"
            value={iocUI.minFileSize}
            onChange={numberChangeHandler}
            onBlur={blurHandler}
            type="number"
          />
          <Field
            label="Max File Size in bytes"
            tooltipText="Min: 20800000, Max: 62400000"
            width="220px"
            name="maxFileSize"
            value={iocUI.maxFileSize}
            onChange={numberChangeHandler}
            onBlur={blurHandler}
            type="number"
          />
        </div>

        <div className="agent-policy-form__line">
          <Field
            label="CFBox Scan Threshold"
            tooltipText="Min: 200, Max: 600"
            width="220px"
            name="cfboxScanThreshold"
            value={iocUI.cfboxScanThreshold}
            onChange={numberChangeHandler}
            onBlur={blurHandler}
            type="number"
          />
          <Field
            label="Fallback URL"
            tooltipText="URL used to fetch IOCs when the local API is unavailable"
            width="300px"
            name="fallbackUrl"
            value={iocUI.fallbackUrl}
            onChange={textChangeHandler}
            placeholder="https://storage.googleapis.com/cfsboxbucket/iocs.txt"
          />
        </div>

        {renderCsvList(
          "fileExtensions",
          "File Extension",
          "File types eligible for IOC scanning",
          "Enter an extension (e.g., exe, dll, dat)",
        )}

        <button
          className="primary-button"
          disabled={checkSaveButtonDisabled()}
          onClick={updateAgentSettingsHandler}>
          Save
        </button>

        <Backdrop
          spinner={false}
          absolute
          show={!policy?.settings?.ioc?.enabled}
          message="IOC settings are inactive! Please click on the status button at the header to activate ioc settings."
          messageStyle={{ paddingTop: 0 }}
        />
      </form>
    </>
  );
};

export default IOCForm;
